import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import axios from 'axios';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';

import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Drawer,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Row,
    Segmented,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tabs,
    Tag,
    Timeline,
    Typography,
    Alert,
    Tooltip,
    message
} from 'antd';

import {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    UserOutlined,
    PhoneOutlined,
    ClockCircleOutlined,
    SoundOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    FileTextOutlined,
    LinkOutlined,
    EyeOutlined,
    DownloadOutlined,
    EnvironmentOutlined,
    CalendarOutlined,
    CloseOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    SaveOutlined,
    DeleteOutlined
} from '@ant-design/icons';

import Layout from './Layout';
import HrDealsList from './HrDealsList';
import {
    HR_DEFAULT_FILTER_FIELDS,
    HR_DEFAULT_PANE_LAYOUT,
    HR_FILTER_FIELD_OPTIONS,
    clampHrPane,
    parseHrDateRange,
    serializeHrFilters
} from '../utils/hrDealsView';
import './TaskManagerFilter.css';
import './HrDealsKanbanPage.css';

dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const formatDealEventWhen = item => {
    if (!item?.starts_at) return '—';
    const start = dayjs(item.starts_at);
    const end = item.ends_at ? dayjs(item.ends_at) : start;
    if (item.all_day) {
        if (start.isSame(end, 'day')) {
            return start.format('DD.MM.YYYY');
        }
        return `${start.format('DD.MM.YYYY')} — ${end.format('DD.MM.YYYY')}`;
    }
    if (start.isSame(end, 'day')) {
        return `${start.format('DD.MM.YYYY HH:mm')} — ${end.format('HH:mm')}`;
    }
    return `${start.format('DD.MM.YYYY HH:mm')} — ${end.format('DD.MM.YYYY HH:mm')}`;
};

const getCurrentMonthRange = () => [
    dayjs().startOf('month'),
    dayjs().endOf('month')
];

const buildDateRangePresets = () => [
    {
        label: 'День',
        value: [dayjs().startOf('day'), dayjs().endOf('day')]
    },
    {
        label: 'Неделя',
        value: [
            dayjs().startOf('isoWeek'),
            dayjs().endOf('isoWeek')
        ]
    },
    {
        label: 'Месяц',
        value: [
            dayjs().startOf('month'),
            dayjs().endOf('month')
        ]
    },
    {
        label: 'Квартал',
        value: [
            dayjs().startOf('quarter'),
            dayjs().endOf('quarter')
        ]
    },
    {
        label: 'Год',
        value: [
            dayjs().startOf('year'),
            dayjs().endOf('year')
        ]
    }
];

const onlyPhoneDigits = value =>
    String(value || '').replace(/\D+/g, '');

const formatDurationSec = value => {
    const total = Math.max(0, Math.floor(Number(value) || 0));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const HrDealsKanbanPage = () => {
    const [loading, setLoading] = useState(false);
    const [stages, setStages] = useState([]);
    const [total, setTotal] = useState(0);
    const [bitrixUsers, setBitrixUsers] = useState([]);

    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const [fioFilter, setFioFilter] = useState('');
    const [fioFilterApplied, setFioFilterApplied] =
        useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [phoneFilterApplied, setPhoneFilterApplied] =
        useState('');
    const [stageFilter, setStageFilter] =
        useState(undefined);
    const [assignedByFilter, setAssignedByFilter] =
        useState(undefined);
    const [sourceFilter, setSourceFilter] =
        useState(undefined);
    const [vacancyFilter, setVacancyFilter] =
        useState(undefined);
    const [vacancyFilterApplied, setVacancyFilterApplied] =
        useState(undefined);
    const [vacancyOptions, setVacancyOptions] = useState([]);
    const [dateType, setDateType] = useState('created');
    const [openDealFromQueryDone, setOpenDealFromQueryDone] =
        useState(false);
    const [dateRange, setDateRange] = useState(
        getCurrentMonthRange
    );
    const [hasCallOnly, setHasCallOnly] = useState(false);
    const [hideClosed, setHideClosed] = useState(true);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [boardView, setBoardView] = useState('kanban');
    const [detailInDrawer, setDetailInDrawer] = useState(false);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 768px)').matches
            : false
    );
    const [paneLayout, setPaneLayout] = useState({
        ...HR_DEFAULT_PANE_LAYOUT
    });
    const paneLayoutRef = useRef({ ...HR_DEFAULT_PANE_LAYOUT });
    const [draftFilterFields, setDraftFilterFields] = useState([
        ...HR_DEFAULT_FILTER_FIELDS
    ]);
    const [filterTemplates, setFilterTemplates] = useState([]);
    const [activeFilterTemplateId, setActiveFilterTemplateId] =
        useState(null);
    const [templateModalOpen, setTemplateModalOpen] =
        useState(false);
    const [templateName, setTemplateName] = useState('');
    const [templateSaving, setTemplateSaving] = useState(false);
    const [viewReady, setViewReady] = useState(false);
    const skipBoardFetchRef = useRef(true);
    const viewConfigRef = useRef({});
    const [sources, setSources] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [colleagues, setColleagues] = useState([]);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm] = Form.useForm();
    const [creating, setCreating] = useState(false);

    const [dealOpen, setDealOpen] = useState(false);
    const [dealLoading, setDealLoading] = useState(false);
    const [currentDeal, setCurrentDeal] = useState(null);
    const [editForm] = Form.useForm();
    const [savingDeal, setSavingDeal] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentSaving, setCommentSaving] = useState(false);
    const [eventOpen, setEventOpen] = useState(false);
    const [eventSaving, setEventSaving] = useState(false);
    const [eventForm] = Form.useForm();
    const eventAllDay = Form.useWatch('all_day', eventForm);

    const [dealCalls, setDealCalls] = useState([]);
    const [dealCallsLoading, setDealCallsLoading] =
        useState(false);
    const [dealCallsError, setDealCallsError] = useState('');
    const [audioUrls, setAudioUrls] = useState({});
    const [dealCallsCache, setDealCallsCache] = useState({});

    const [dragDealId, setDragDealId] = useState(null);
    const [dragOverStageId, setDragOverStageId] =
        useState(null);
    const [boardPanning, setBoardPanning] = useState(false);
    const boardRef = useRef(null);
    const panRef = useRef({
        active: false,
        startX: 0,
        startScroll: 0,
        moved: false
    });

    useEffect(() => {
        const onMove = event => {
            const pan = panRef.current;
            if (!pan.active || !boardRef.current) {
                return;
            }
            const dx = event.clientX - pan.startX;
            if (Math.abs(dx) > 3) {
                pan.moved = true;
            }
            boardRef.current.scrollLeft = pan.startScroll - dx;
            event.preventDefault();
        };

        const stopPan = () => {
            if (!panRef.current.active) {
                return;
            }
            panRef.current.active = false;
            setBoardPanning(false);
        };

        window.addEventListener('mousemove', onMove, {
            passive: false
        });
        window.addEventListener('mouseup', stopPan);
        window.addEventListener('blur', stopPan);

        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', stopPan);
            window.removeEventListener('blur', stopPan);
        };
    }, []);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const onChange = event => setIsMobile(event.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);

    const [resumePreview, setResumePreview] = useState({
        open: false,
        loading: false,
        url: null,
        mime: null,
        name: null
    });

    const [avitoStatus, setAvitoStatus] = useState(null);
    const [avitoBusy, setAvitoBusy] = useState(false);
    const [hhStatus, setHhStatus] = useState(null);
    const [hhBusy, setHhBusy] = useState(false);

    const fetchAvitoStatus = useCallback(async () => {
        try {
            const response = await axios.get(
                '/api/hr/avito/status',
                {
                    headers: authHeaders(),
                    withCredentials: true
                }
            );
            setAvitoStatus(response.data || null);
        } catch (_error) {
            setAvitoStatus(null);
        }
    }, []);

    const fetchHhStatus = useCallback(async () => {
        try {
            const response = await axios.get(
                '/api/hr/hh/status',
                {
                    headers: authHeaders(),
                    withCredentials: true
                }
            );
            setHhStatus(response.data || null);
        } catch (_error) {
            setHhStatus(null);
        }
    }, []);

    const runAvitoFetch = async () => {
        setAvitoBusy(true);

        try {
            const response = await axios.post(
                '/api/hr/avito/fetch',
                {
                    create_deals: false,
                    max_pages: 5
                },
                {
                    headers: authHeaders(),
                    withCredentials: true
                }
            );

            const data = response.data || {};
            const sync = data.sync || {};

            message.success(
                `Avito: токен OK, откликов +${sync.created || 0} / ~${sync.updated || 0}, резюме ${sync.resumes_synced || 0}`
            );
            await fetchAvitoStatus();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось получить отклики Avito'
            );
        } finally {
            setAvitoBusy(false);
        }
    };

    const runHhFetch = async () => {
        setHhBusy(true);

        try {
            const response = await axios.post(
                '/api/hr/hh/fetch',
                {
                    create_deals: false,
                    max_vacancies: 10
                },
                {
                    headers: authHeaders(),
                    withCredentials: true
                }
            );

            const data = response.data || {};
            const sync = data.sync || {};

            message.success(
                `HH: откликов +${sync.created || 0} / ~${sync.updated || 0}, резюме ${sync.resumes_synced || 0}`
            );
            await fetchHhStatus();
        } catch (error) {
            const payload = error.response?.data || {};

            if (
                error.response?.status === 401 &&
                payload.authorize_url
            ) {
                message.warning(
                    'Нужен вход менеджера HH — откроется OAuth, затем нажмите «HH получить» ещё раз'
                );
                window.open(
                    payload.authorize_url,
                    '_blank',
                    'noopener,noreferrer'
                );
            } else {
                message.error(
                    payload.message ||
                        'Не удалось получить отклики HH'
                );
            }
        } finally {
            setHhBusy(false);
        }
    };

    const fetchBoard = useCallback(async () => {
        setLoading(true);

        try {
            if (phoneFilterApplied) {
                try {
                    await axios.post(
                        '/api/ats/calls/sync',
                        {
                            phone: phoneFilterApplied,
                            start: dateRange?.[0]
                                ? dateRange[0]
                                      .startOf('day')
                                      .toISOString()
                                : undefined,
                            end: dateRange?.[1]
                                ? dateRange[1]
                                      .endOf('day')
                                      .toISOString()
                                : undefined
                        },
                        { headers: authHeaders() }
                    );
                } catch (syncError) {
                    console.warn(
                        'ATS phone sync failed',
                        syncError
                    );
                }
            }

            const response = await axios.get(
                '/api/hr/board',
                {
                    params: {
                        search:
                            searchApplied || undefined,
                        fio:
                            fioFilterApplied || undefined,
                        phone:
                            phoneFilterApplied ||
                            undefined,
                        stage_id:
                            stageFilter || undefined,
                        assigned_by_id:
                            assignedByFilter || undefined,
                        source_id:
                            sourceFilter || undefined,
                        vacancy:
                            vacancyFilterApplied || undefined,
                        date_type: dateType || 'created',
                        date_from: dateRange?.[0]
                            ? dateRange[0].format(
                                  'YYYY-MM-DD'
                              )
                            : undefined,
                        date_to: dateRange?.[1]
                            ? dateRange[1].format(
                                  'YYYY-MM-DD'
                              )
                            : undefined,
                        has_call: hasCallOnly
                            ? '1'
                            : undefined,
                        hide_closed: hideClosed ? '1' : '0'
                    },
                    headers: authHeaders()
                }
            );

            setStages(response.data?.stages || []);
            setTotal(response.data?.total || 0);

            const nextCallsCache = {};

            (response.data?.stages || []).forEach(stage => {
                (stage.deals || []).forEach(deal => {
                    if (!deal?.id) {
                        return;
                    }

                    nextCallsCache[deal.id] = {
                        count: Number(deal.calls_count) || 0,
                        recorded:
                            Number(deal.calls_recorded) || 0,
                        lastDate: deal.last_call_at || null
                    };
                });
            });

            setDealCallsCache(prev => ({
                ...prev,
                ...nextCallsCache
            }));
        } catch (error) {
            console.error(error);
            message.error(
                error.response?.data?.message ||
                    'Не удалось загрузить канбан Bitrix'
            );
        } finally {
            setLoading(false);
        }
    }, [
        searchApplied,
        fioFilterApplied,
        phoneFilterApplied,
        stageFilter,
        assignedByFilter,
        sourceFilter,
        vacancyFilterApplied,
        dateType,
        dateRange,
        hasCallOnly,
        hideClosed
    ]);

    const fetchMeta = useCallback(async () => {
        try {
            const [usersRes, sourcesRes, companiesRes, colleaguesRes, vacanciesRes] =
                await Promise.all([
                    axios.get('/api/hr/users', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/hr/sources', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/hr/companies', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/calendar/colleagues', {
                        headers: authHeaders()
                    }),
                    axios.get('/api/bitrix/analytics/vacancies', {
                        headers: authHeaders()
                    }).catch(() => ({ data: [] }))
                ]);

            setBitrixUsers(usersRes.data || []);
            setSources(sourcesRes.data || []);
            setCompanies(companiesRes.data || []);
            setColleagues(colleaguesRes.data?.items || []);
            setVacancyOptions(
                (Array.isArray(vacanciesRes.data)
                    ? vacanciesRes.data
                    : []
                ).map(item => ({
                    value: item.value || item.label || item,
                    label: item.label || item.value || item
                }))
            );
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        fetchMeta();
        fetchAvitoStatus();
        fetchHhStatus();
    }, [fetchMeta, fetchAvitoStatus, fetchHhStatus]);

    const userOptions = useMemo(
        () =>
            bitrixUsers.map(user => ({
                value: user.id,
                label:
                    user.full_name ||
                    `${user.name || ''} ${user.last_name || ''}`.trim() ||
                    `#${user.id}`
            })),
        [bitrixUsers]
    );

    const sourceOptions = useMemo(
        () =>
            sources.map(source => ({
                value: source.id,
                label: source.name || `#${source.id}`
            })),
        [sources]
    );

    const companyOptions = useMemo(
        () =>
            companies.map(company => ({
                value: company.id,
                label: company.name || `#${company.id}`
            })),
        [companies]
    );

    const stageOptions = useMemo(
        () =>
            stages
                .filter(stage => stage.id !== '__unknown__')
                .map(stage => ({
                    value: stage.id,
                    label: stage.name
                })),
        [stages]
    );

    const flatDeals = useMemo(
        () =>
            stages.flatMap(stage =>
                (stage.deals || []).map(deal => ({
                    ...deal,
                    stage_id: deal.stage_id || stage.id,
                    stage_name:
                        deal.stage_name || stage.name,
                    stage_color: stage.color || '#1677ff'
                }))
            ),
        [stages]
    );

    const buildCurrentFiltersPayload = useCallback(
        () =>
            serializeHrFilters({
                fio: fioFilter,
                phone: phoneFilter,
                stageId: stageFilter,
                assignedById: assignedByFilter,
                sourceId: sourceFilter,
                vacancy: vacancyFilter,
                dateType,
                dateRange,
                hasCallOnly,
                hideClosed,
                search
            }),
        [
            fioFilter,
            phoneFilter,
            stageFilter,
            assignedByFilter,
            sourceFilter,
            vacancyFilter,
            dateType,
            dateRange,
            hasCallOnly,
            hideClosed,
            search
        ]
    );

    const buildViewConfig = useCallback(
        (patch = {}) => {
            const next = {
                filterFields: draftFilterFields,
                filters: buildCurrentFiltersPayload(),
                filterTemplates,
                boardView,
                detailInDrawer,
                filtersOpen,
                paneLayout: { ...paneLayoutRef.current },
                ...patch
            };
            viewConfigRef.current = next;
            return next;
        },
        [
            draftFilterFields,
            buildCurrentFiltersPayload,
            filterTemplates,
            boardView,
            detailInDrawer,
            filtersOpen
        ]
    );

    const persistViewConfig = useCallback(
        async patch => {
            const next = buildViewConfig(patch);
            try {
                await axios.put(
                    '/api/hr/my-view',
                    { config: next },
                    { headers: authHeaders() }
                );
            } catch (_error) {
                /* ignore */
            }
            return next;
        },
        [buildViewConfig]
    );

    const applyFiltersFromConfig = useCallback(filters => {
        const next = filters || {};
        const fio = String(next.fio || '');
        const phone = String(next.phone || '');
        const vacancy = next.vacancy
            ? String(next.vacancy)
            : undefined;
        const searchValue = String(next.search || '');

        setSearch(searchValue);
        setSearchApplied(searchValue.trim());
        setFioFilter(fio);
        setFioFilterApplied(fio.trim());
        setPhoneFilter(phone);
        setPhoneFilterApplied(onlyPhoneDigits(phone));
        setStageFilter(next.stage_id || undefined);
        setAssignedByFilter(next.assigned_by_id || undefined);
        setSourceFilter(next.source_id || undefined);
        setVacancyFilter(vacancy);
        setVacancyFilterApplied(
            vacancy ? String(vacancy).trim() : undefined
        );
        setDateType(next.date_type || 'created');
        setDateRange(parseHrDateRange(next));
        setHasCallOnly(Boolean(next.has_call));
        setHideClosed(next.hide_closed !== false);
    }, []);

    const loadMyView = useCallback(async () => {
        try {
            const response = await axios.get('/api/hr/my-view', {
                headers: authHeaders()
            });
            const cfg = response.data?.data?.config || {};
            const panes = {
                ...HR_DEFAULT_PANE_LAYOUT,
                ...(cfg.paneLayout || {})
            };
            panes.filterPx = clampHrPane(panes.filterPx, 200, 480);
            panes.detailPx = clampHrPane(panes.detailPx, 300, 720);

            const fields =
                Array.isArray(cfg.filterFields) &&
                cfg.filterFields.length
                    ? cfg.filterFields
                    : [...HR_DEFAULT_FILTER_FIELDS];

            skipBoardFetchRef.current = true;
            setPaneLayout(panes);
            paneLayoutRef.current = panes;
            setDraftFilterFields(fields);
            setFilterTemplates(
                Array.isArray(cfg.filterTemplates)
                    ? cfg.filterTemplates
                    : []
            );
            setBoardView(
                cfg.boardView === 'list' ? 'list' : 'kanban'
            );
            setDetailInDrawer(Boolean(cfg.detailInDrawer));
            setFiltersOpen(cfg.filtersOpen !== false);
            applyFiltersFromConfig(cfg.filters || {});
            viewConfigRef.current = {
                ...cfg,
                filterFields: fields,
                paneLayout: panes
            };
        } catch (error) {
            console.warn('HR my-view load failed', error);
        } finally {
            skipBoardFetchRef.current = false;
            setViewReady(true);
        }
    }, [applyFiltersFromConfig]);

    const startPaneResize = useCallback(
        (which, event) => {
            event.preventDefault();
            event.stopPropagation();
            const startX = event.clientX;
            const start =
                which === 'filter'
                    ? paneLayoutRef.current.filterPx
                    : paneLayoutRef.current.detailPx;
            const totalWidth =
                event.currentTarget.parentElement
                    ?.clientWidth || window.innerWidth;

            document.body.classList.add('tm-pane-resizing');

            const onMove = moveEvent => {
                const delta = moveEvent.clientX - startX;
                if (which === 'filter') {
                    const nextFilter = clampHrPane(
                        start + delta,
                        200,
                        Math.min(480, totalWidth * 0.45)
                    );
                    const next = {
                        ...paneLayoutRef.current,
                        filterPx: nextFilter
                    };
                    paneLayoutRef.current = next;
                    setPaneLayout(next);
                    return;
                }

                const nextDetail = clampHrPane(
                    start - delta,
                    300,
                    Math.min(720, totalWidth * 0.55)
                );
                const next = {
                    ...paneLayoutRef.current,
                    detailPx: nextDetail
                };
                paneLayoutRef.current = next;
                setPaneLayout(next);
            };

            const onUp = () => {
                document.body.classList.remove(
                    'tm-pane-resizing'
                );
                window.removeEventListener(
                    'mousemove',
                    onMove
                );
                window.removeEventListener('mouseup', onUp);
                persistViewConfig({
                    paneLayout: { ...paneLayoutRef.current }
                });
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [persistViewConfig]
    );

    const applyFilters = async () => {
        setSearchApplied(search.trim());
        setFioFilterApplied(fioFilter.trim());
        setPhoneFilterApplied(
            onlyPhoneDigits(phoneFilter)
        );
        setVacancyFilterApplied(
            vacancyFilter
                ? String(vacancyFilter).trim()
                : undefined
        );
        await persistViewConfig({
            filterFields: draftFilterFields,
            filters: serializeHrFilters({
                fio: fioFilter,
                phone: phoneFilter,
                stageId: stageFilter,
                assignedById: assignedByFilter,
                sourceId: sourceFilter,
                vacancy: vacancyFilter,
                dateType,
                dateRange,
                hasCallOnly,
                hideClosed,
                search
            })
        });
    };

    const resetBoardFilters = async () => {
        const range = getCurrentMonthRange();
        setSearch('');
        setSearchApplied('');
        setFioFilter('');
        setFioFilterApplied('');
        setPhoneFilter('');
        setPhoneFilterApplied('');
        setStageFilter(undefined);
        setAssignedByFilter(undefined);
        setSourceFilter(undefined);
        setVacancyFilter(undefined);
        setVacancyFilterApplied(undefined);
        setDateType('created');
        setDateRange(range);
        setHasCallOnly(false);
        setHideClosed(true);
        setActiveFilterTemplateId(null);
        await persistViewConfig({
            filters: serializeHrFilters({
                dateType: 'created',
                dateRange: range,
                hasCallOnly: false,
                hideClosed: true
            })
        });
    };

    const saveFilterTemplate = async () => {
        const name = String(templateName || '').trim();
        if (!name) {
            message.warning('Введите название шаблона');
            return;
        }
        setTemplateSaving(true);
        try {
            const item = {
                id: `ft_${Date.now()}`,
                name,
                filterFields: [...draftFilterFields],
                filters: buildCurrentFiltersPayload(),
                createdAt: new Date().toISOString()
            };
            const nextTemplates = [...filterTemplates, item];
            setFilterTemplates(nextTemplates);
            setActiveFilterTemplateId(item.id);
            setTemplateModalOpen(false);
            setTemplateName('');
            await persistViewConfig({
                filterTemplates: nextTemplates
            });
            message.success('Шаблон сохранён');
        } catch (_error) {
            message.error('Не удалось сохранить шаблон');
        } finally {
            setTemplateSaving(false);
        }
    };

    const applyFilterTemplate = async templateId => {
        const item = filterTemplates.find(
            t => t.id === templateId
        );
        if (!item) {
            return;
        }
        setActiveFilterTemplateId(templateId);
        if (Array.isArray(item.filterFields) && item.filterFields.length) {
            setDraftFilterFields(item.filterFields);
        }
        applyFiltersFromConfig(item.filters || {});
        await persistViewConfig({
            filterFields:
                item.filterFields?.length
                    ? item.filterFields
                    : draftFilterFields,
            filters: item.filters || {}
        });
    };

    const deleteFilterTemplate = () => {
        if (!activeFilterTemplateId) {
            return;
        }
        const id = activeFilterTemplateId;
        const item = filterTemplates.find(t => t.id === id);
        if (!item) {
            return;
        }
        Modal.confirm({
            title: `Удалить шаблон «${item.name}»?`,
            okText: 'Удалить',
            okButtonProps: { danger: true },
            cancelText: 'Отмена',
            onOk: async () => {
                const nextTemplates = filterTemplates.filter(
                    t => t.id !== id
                );
                setFilterTemplates(nextTemplates);
                setActiveFilterTemplateId(null);
                await persistViewConfig({
                    filterTemplates: nextTemplates
                });
            }
        });
    };

    useEffect(() => {
        loadMyView();
    }, [loadMyView]);

    useEffect(() => {
        if (skipBoardFetchRef.current || !viewReady) {
            return;
        }
        fetchBoard();
    }, [fetchBoard, viewReady]);


    const revokeAudioUrls = urls => {
        Object.values(urls || {}).forEach(url => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        });
    };

    const loadRecordingAudio = async filename => {
        if (!filename) {
            return null;
        }

        if (audioUrls[filename]) {
            return audioUrls[filename];
        }

        const response = await axios.get(
            '/api/ats/call-records/file',
            {
                headers: authHeaders(),
                params: { filename },
                responseType: 'blob'
            }
        );

        const blobUrl = URL.createObjectURL(response.data);

        setAudioUrls(prev => ({
            ...prev,
            [filename]: blobUrl
        }));

        return blobUrl;
    };

    const loadDealCalls = async (
        dealId,
        { refresh = true } = {}
    ) => {
        setDealCalls([]);
        setDealCallsError('');
        setDealCallsLoading(true);

        try {
            const response = await axios.get(
                `/api/ats/deals/${dealId}/calls`,
                {
                    headers: authHeaders(),
                    params: {
                        refresh: refresh ? '1' : '0'
                    }
                }
            );

            const calls = Array.isArray(response.data?.calls)
                ? response.data.calls
                : [];

            setDealCalls(calls);

            if (response.data?.warning) {
                setDealCallsError(response.data.warning);
            }

            setDealCallsCache(prev => ({
                ...prev,
                [dealId]: {
                    count: calls.length,
                    recorded: calls.filter(
                        item => item.has_recording
                    ).length,
                    lastDate: calls[0]?.date || null
                }
            }));
        } catch (error) {
            console.error(error);
            setDealCallsError(
                error.response?.data?.error ||
                    error.message ||
                    'Не удалось загрузить звонки АТС'
            );
        } finally {
            setDealCallsLoading(false);
        }
    };

    const closeResumePreview = () => {
        setResumePreview(prev => {
            if (prev.url) {
                URL.revokeObjectURL(prev.url);
            }

            return {
                open: false,
                loading: false,
                url: null,
                mime: null,
                name: null
            };
        });
    };

    const readAxiosErrorMessage = async (error, fallback) => {
        const data = error?.response?.data;

        if (data instanceof Blob) {
            try {
                const text = await data.text();
                const parsed = JSON.parse(text);
                return parsed.message || parsed.error || fallback;
            } catch (_error) {
                return fallback;
            }
        }

        if (typeof data === 'string' && data.trim()) {
            return data;
        }

        return data?.message || data?.error || error?.message || fallback;
    };

    const loadResumeBlob = async dealId => {
        const response = await axios.get(
            `/api/hr/deals/${dealId}/resume/file`,
            {
                headers: authHeaders(),
                responseType: 'blob'
            }
        );

        const headerMime = String(
            response.headers['content-type'] || ''
        )
            .split(';')[0]
            .trim()
            .toLowerCase();

        if (
            headerMime.includes('application/json') ||
            headerMime.includes('text/html')
        ) {
            let messageText =
                'Не удалось загрузить файл резюме';

            try {
                const text = await response.data.text();
                const parsed = JSON.parse(text);
                messageText =
                    parsed.message || parsed.error || messageText;
            } catch (_error) {
                // ignore
            }

            throw Object.assign(new Error(messageText), {
                status: response.status
            });
        }

        const mime =
            headerMime ||
            currentDeal?.resume?.mime_type ||
            'application/octet-stream';

        const blob = new Blob([response.data], { type: mime });
        const url = URL.createObjectURL(blob);
        const name =
            currentDeal?.resume?.original_name ||
            `resume-${dealId}`;

        return { url, mime, name, blob };
    };

    const openLocalResumeFile = async (dealId, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();

        const hide = message.loading('Загрузка резюме…', 0);

        try {
            const file = await loadResumeBlob(dealId);

            // Открываем после завершения click-события,
            // иначе маска модалки ловит тот же клик и сразу закрывается.
            window.setTimeout(() => {
                setResumePreview(prev => {
                    if (prev.url) {
                        URL.revokeObjectURL(prev.url);
                    }

                    return {
                        open: true,
                        loading: false,
                        url: file.url,
                        mime: file.mime,
                        name: file.name
                    };
                });
            }, 0);
        } catch (error) {
            message.error(
                await readAxiosErrorMessage(
                    error,
                    'Не удалось открыть файл резюме'
                )
            );
        } finally {
            hide();
        }
    };

    const downloadLocalResumeFile = async (dealId, event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();

        const hide = message.loading('Скачивание резюме…', 0);

        try {
            const file = await loadResumeBlob(dealId);
            const link = document.createElement('a');
            link.href = file.url;
            link.download = file.name || `resume-${dealId}`;
            link.rel = 'noopener';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(
                () => URL.revokeObjectURL(file.url),
                30_000
            );
            message.success('Файл скачан');
        } catch (error) {
            message.error(
                await readAxiosErrorMessage(
                    error,
                    'Не удалось скачать файл резюме'
                )
            );
        } finally {
            hide();
        }
    };

    const refreshDealResume = async dealId => {
        try {
            const response = await axios.post(
                `/api/hr/deals/${dealId}/resume/sync`,
                {},
                { headers: authHeaders() }
            );

            setCurrentDeal(prev =>
                prev
                    ? {
                          ...prev,
                          resume: response.data
                      }
                    : prev
            );
            message.success('Резюме обновлено из Bitrix');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось обновить резюме'
            );
        }
    };

    const closeDealPanel = () => {
        setDealOpen(false);
        setCurrentDeal(null);
        revokeAudioUrls(audioUrls);
        setAudioUrls({});
        setDealCalls([]);
        setDealCallsError('');
        closeResumePreview();
        setEventOpen(false);
    };

    const openDeal = async dealId => {
        setDealOpen(true);
        setDealLoading(true);
        setCommentText('');
        revokeAudioUrls(audioUrls);
        setAudioUrls({});
        setDealCalls([]);
        setDealCallsError('');
        loadDealCalls(dealId, { refresh: true });

        try {
            const response = await axios.get(
                `/api/hr/deals/${dealId}`,
                { headers: authHeaders() }
            );

            const deal = response.data;
            setCurrentDeal(deal);
            editForm.setFieldsValue({
                title: deal.title,
                stage_id: deal.stage_id,
                assigned_by_id: deal.assigned_by_id,
                source_id: deal.source_id,
                city: deal.city,
                contact_phone: deal.contact_phone
            });
        } catch (error) {
            console.error(error);
            message.error('Не удалось открыть сделку');
            setDealOpen(false);
        } finally {
            setDealLoading(false);
        }
    };

    useEffect(() => {
        if (openDealFromQueryDone) {
            return;
        }

        const params = new URLSearchParams(
            window.location.search
        );
        const dealId = params.get('deal');
        if (!dealId) {
            return;
        }

        setOpenDealFromQueryDone(true);
        openDeal(dealId).finally(() => {
            const next = new URL(window.location.href);
            next.searchParams.delete('deal');
            window.history.replaceState(
                {},
                '',
                `${next.pathname}${next.search}`
            );
        });
    }, [openDealFromQueryDone]);

    const createDeal = async () => {
        try {
            const values =
                await createForm.validateFields();
            setCreating(true);

            const response = await axios.post(
                '/api/hr/deals',
                values,
                { headers: authHeaders() }
            );

            message.success('Сделка создана в Bitrix');
            setCreateOpen(false);
            createForm.resetFields();
            await fetchBoard();
            await fetchMeta();

            if (response.data?.id) {
                openDeal(response.data.id);
            }
        } catch (error) {
            if (error?.errorFields) {
                return;
            }

            message.error(
                error.response?.data?.message ||
                    'Не удалось создать сделку'
            );
        } finally {
            setCreating(false);
        }
    };

    const saveDeal = async () => {
        if (!currentDeal?.id) {
            return;
        }

        try {
            const values =
                await editForm.validateFields();
            setSavingDeal(true);

            const stageChanged =
                values.stage_id &&
                String(values.stage_id) !==
                    String(currentDeal.stage_id);

            await axios.put(
                `/api/hr/deals/${currentDeal.id}`,
                values,
                { headers: authHeaders() }
            );

            if (stageChanged) {
                await axios.put(
                    `/api/hr/deals/${currentDeal.id}/move`,
                    { stage_id: values.stage_id },
                    { headers: authHeaders() }
                );
            }

            message.success('Сделка сохранена');
            await openDeal(currentDeal.id);
            await fetchBoard();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }

            message.error(
                error.response?.data?.message ||
                    'Не удалось сохранить'
            );
        } finally {
            setSavingDeal(false);
        }
    };

    const addComment = async () => {
        if (!currentDeal?.id || !commentText.trim()) {
            return;
        }

        setCommentSaving(true);

        try {
            await axios.post(
                `/api/hr/deals/${currentDeal.id}/comments`,
                { text: commentText.trim() },
                { headers: authHeaders() }
            );

            setCommentText('');
            await openDeal(currentDeal.id);
            message.success('Комментарий добавлен');
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось добавить комментарий'
            );
        } finally {
            setCommentSaving(false);
        }
    };

    const openEventModal = () => {
        if (!currentDeal) return;
        const start = dayjs().add(1, 'hour').minute(0).second(0);
        eventForm.setFieldsValue({
            type: 'meeting',
            title: `Интервью: ${currentDeal.title}`,
            all_day: false,
            range: [start, start.add(1, 'hour')],
            location: currentDeal.city || '',
            description: [
                `Сделка Bitrix #${currentDeal.id}`,
                currentDeal.contact_phone
                    ? `Телефон: ${currentDeal.contact_phone}`
                    : null,
                currentDeal.city
                    ? `Город: ${currentDeal.city}`
                    : null
            ]
                .filter(Boolean)
                .join('\n'),
            participant_ids: []
        });
        setEventOpen(true);
    };

    const createDealEvent = async () => {
        try {
            const values = await eventForm.validateFields();
            const [start, end] = values.range || [];
            if (!start || !end) {
                message.error('Укажите время');
                return;
            }
            setEventSaving(true);
            await axios.post(
                '/api/calendar',
                {
                    title: values.title,
                    type: values.type,
                    all_day: Boolean(values.all_day),
                    starts_at: values.all_day
                        ? start.startOf('day').toISOString()
                        : start.toISOString(),
                    ends_at: values.all_day
                        ? end.endOf('day').toISOString()
                        : end.toISOString(),
                    location: values.location || null,
                    description: values.description || null,
                    participant_ids: values.participant_ids || [],
                    bitrix_deal_id: currentDeal?.id || null
                },
                { headers: authHeaders() }
            );
            message.success('Событие добавлено в календарь');
            setEventOpen(false);
            if (currentDeal?.id) {
                await openDeal(currentDeal.id);
            }
            fetchBoard();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error.response?.data?.error ||
                    'Не удалось создать событие'
            );
        } finally {
            setEventSaving(false);
        }
    };

    const deleteDeal = async () => {
        if (!currentDeal?.id) {
            return;
        }

        try {
            await axios.delete(
                `/api/hr/deals/${currentDeal.id}`,
                { headers: authHeaders() }
            );

            message.success('Сделка удалена');
            setDealOpen(false);
            setCurrentDeal(null);
            fetchBoard();
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось удалить'
            );
        }
    };

    const onDropToStage = async stageId => {
        if (!dragDealId || stageId === '__unknown__') {
            setDragDealId(null);
            setDragOverStageId(null);
            return;
        }

        const fromStage = stages.find(stage =>
            (stage.deals || []).some(
                deal =>
                    String(deal.id) ===
                    String(dragDealId)
            )
        );

        const deal = (fromStage?.deals || []).find(
            item =>
                String(item.id) === String(dragDealId)
        );

        setDragDealId(null);
        setDragOverStageId(null);

        if (
            !deal ||
            String(deal.stage_id) === String(stageId)
        ) {
            return;
        }

        const targetStage = stages.find(
            stage => String(stage.id) === String(stageId)
        );

        setStages(prev =>
            prev.map(stage => {
                const deals = (stage.deals || []).filter(
                    item =>
                        String(item.id) !==
                        String(deal.id)
                );

                if (String(stage.id) === String(stageId)) {
                    return {
                        ...stage,
                        deals: [
                            {
                                ...deal,
                                stage_id: stageId,
                                stage_name:
                                    targetStage?.name ||
                                    deal.stage_name
                            },
                            ...deals
                        ]
                    };
                }

                return {
                    ...stage,
                    deals
                };
            })
        );

        try {
            await axios.put(
                `/api/hr/deals/${deal.id}/move`,
                { stage_id: stageId },
                { headers: authHeaders() }
            );
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Не удалось переместить сделку в Bitrix'
            );
            fetchBoard();
        }
    };


    const renderHrFilterField = fieldKey => {
        switch (fieldKey) {
            case 'fio':
                return (
                    <Input
                        allowClear
                        prefix={<UserOutlined />}
                        placeholder="ФИО кандидата"
                        value={fioFilter}
                        onChange={e => setFioFilter(e.target.value)}
                        onPressEnter={applyFilters}
                    />
                );
            case 'stage_id':
                return (
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Стадия"
                        options={stageOptions}
                        value={stageFilter}
                        onChange={setStageFilter}
                    />
                );
            case 'assigned_by_id':
                return (
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Ответственный"
                        options={userOptions}
                        value={assignedByFilter}
                        onChange={setAssignedByFilter}
                    />
                );
            case 'vacancy':
                return (
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Вакансия (из Bitrix)"
                        options={vacancyOptions}
                        value={vacancyFilter}
                        onChange={setVacancyFilter}
                    />
                );
            case 'period':
                return (
                    <div>
                        <Select
                            value={dateType}
                            onChange={setDateType}
                            options={[
                                {
                                    value: 'created',
                                    label: 'Дата создания'
                                },
                                {
                                    value: 'modified',
                                    label: 'Дата изменения'
                                }
                            ]}
                            style={{
                                width: '100%',
                                marginBottom: 8
                            }}
                        />
                        <RangePicker
                            allowClear={false}
                            format="DD.MM.YYYY"
                            value={dateRange}
                            onChange={value => {
                                if (value?.[0] && value?.[1]) {
                                    setDateRange(value);
                                } else {
                                    setDateRange(
                                        getCurrentMonthRange()
                                    );
                                }
                            }}
                            presets={buildDateRangePresets()}
                            style={{ width: '100%' }}
                        />
                        <Space
                            wrap
                            size={[4, 4]}
                            style={{ marginTop: 8 }}
                        >
                            {buildDateRangePresets().map(preset => {
                                const active =
                                    dateRange?.[0] &&
                                    dateRange?.[1] &&
                                    dateRange[0].isSame(
                                        preset.value[0],
                                        'day'
                                    ) &&
                                    dateRange[1].isSame(
                                        preset.value[1],
                                        'day'
                                    );
                                return (
                                    <Button
                                        key={preset.label}
                                        size="small"
                                        type={
                                            active
                                                ? 'primary'
                                                : 'default'
                                        }
                                        onClick={() =>
                                            setDateRange([
                                                preset.value[0],
                                                preset.value[1]
                                            ])
                                        }
                                    >
                                        {preset.label}
                                    </Button>
                                );
                            })}
                        </Space>
                    </div>
                );
            case 'source_id':
                return (
                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Источник"
                        options={sourceOptions}
                        value={sourceFilter}
                        onChange={setSourceFilter}
                    />
                );
            case 'phone':
                return (
                    <Input
                        allowClear
                        prefix={<PhoneOutlined />}
                        placeholder="Телефон"
                        value={phoneFilter}
                        onChange={e =>
                            setPhoneFilter(e.target.value)
                        }
                        onPressEnter={applyFilters}
                    />
                );
            case 'has_call':
                return (
                    <Space>
                        <Switch
                            checked={hasCallOnly}
                            onChange={setHasCallOnly}
                        />
                        <Text type="secondary">Есть звонок</Text>
                    </Space>
                );
            case 'hide_closed':
                return (
                    <Space>
                        <Switch
                            checked={hideClosed}
                            onChange={setHideClosed}
                        />
                        <Text type="secondary">
                            Скрыть «Выход»
                        </Text>
                    </Space>
                );
            default:
                return null;
        }
    };

    const useDetailDrawer = detailInDrawer || isMobile;
    const splitDetail = dealOpen && !useDetailDrawer;

    const dealDetailTitle = (
                    currentDeal
                        ? `#${currentDeal.id} ${currentDeal.title}`
                        : 'Сделка'
    );

    const dealDetailActions = (
                    <Space wrap>
                        <Button
                            icon={<CalendarOutlined />}
                            onClick={openEventModal}
                            disabled={!currentDeal?.id}
                        >
                            В календарь
                        </Button>
                        <Popconfirm
                            title="Удалить сделку в Bitrix?"
                            onConfirm={deleteDeal}
                        >
                            <Button danger>
                                Удалить
                            </Button>
                        </Popconfirm>
                        <Button
                            type="primary"
                            loading={savingDeal}
                            onClick={saveDeal}
                        >
                            Сохранить
                        </Button>
                    </Space>
    );

    const dealDetailBody = (
                <Spin spinning={dealLoading}>
                    <Tabs
                        defaultActiveKey="overview"
                        items={[
                            {
                                key: 'overview',
                                label: 'Обзор',
                                children: (
                                    <div>
                                        <Space wrap style={{ marginBottom: 12 }}>
                                            {currentDeal?.city ? (
                                                <Tag icon={<EnvironmentOutlined />} color="blue">
                                                    {currentDeal.city}
                                                </Tag>
                                            ) : null}
                                            {currentDeal?.contact_phone ? (
                                                <Tag icon={<PhoneOutlined />}>
                                                    {currentDeal.contact_phone}
                                                </Tag>
                                            ) : null}
                                            {currentDeal?.source_name ? (
                                                <Tag>{currentDeal.source_name}</Tag>
                                            ) : null}
                                        </Space>

                                        <Form
                                            form={editForm}
                                            layout="vertical"
                                            requiredMark={false}
                                        >
                                            <Form.Item
                                                name="title"
                                                label="Название"
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Укажите название'
                                                    }
                                                ]}
                                            >
                                                <Input />
                                            </Form.Item>

                                            <Row gutter={12}>
                                                <Col span={12}>
                                                    <Form.Item name="city" label="Город">
                                                        <Input placeholder="Город кандидата" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="contact_phone"
                                                        label="Телефон"
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                            <Row gutter={12}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="stage_id"
                                                        label="Стадия Bitrix"
                                                    >
                                                        <Select
                                                            options={stages
                                                                .filter(
                                                                    stage =>
                                                                        stage.id !==
                                                                        '__unknown__'
                                                                )
                                                                .map(stage => ({
                                                                    value: stage.id,
                                                                    label: stage.name
                                                                }))}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="assigned_by_id"
                                                        label="Ответственный"
                                                    >
                                                        <Select
                                                            allowClear
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={userOptions}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                        </Form>

                                        {(currentDeal?.calendar_events || [])
                                            .length ? (
                                            <div style={{ marginTop: 16 }}>
                                                <Title
                                                    level={5}
                                                    style={{
                                                        marginBottom: 8
                                                    }}
                                                >
                                                    <CalendarOutlined />{' '}
                                                    События календаря
                                                </Title>
                                                <Space
                                                    direction="vertical"
                                                    size={8}
                                                    style={{ width: '100%' }}
                                                >
                                                    {(
                                                        currentDeal?.calendar_events ||
                                                        []
                                                    ).map(
                                                        ev => (
                                                            <div
                                                                key={ev.id}
                                                                className="hr-deal-calendar-item"
                                                            >
                                                                <Space wrap>
                                                                    <Tag color="purple">
                                                                        {ev.type_name ||
                                                                            ev.type}
                                                                    </Tag>
                                                                    <Text strong>
                                                                        {
                                                                            ev.title
                                                                        }
                                                                    </Text>
                                                                </Space>
                                                                <div>
                                                                    <Text type="secondary">
                                                                        {formatDealEventWhen(
                                                                            ev
                                                                        )}
                                                                        {ev.location
                                                                            ? ` · ${ev.location}`
                                                                            : ''}
                                                                    </Text>
                                                                </div>
                                                                {ev.creator_name ? (
                                                                    <Text type="secondary">
                                                                        {
                                                                            ev.creator_name
                                                                        }
                                                                    </Text>
                                                                ) : null}
                                                            </div>
                                                        )
                                                    )}
                                                </Space>
                                            </div>
                                        ) : currentDeal ? (
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display: 'block',
                                                    marginTop: 8
                                                }}
                                            >
                                                Событий календаря по этой
                                                сделке пока нет
                                            </Text>
                                        ) : null}
                                    </div>
                                )
                            },
                            {
                                key: 'resume',
                                label: 'Резюме',
                                children: (
                                    <div>
                                        <Space
                                            style={{
                                                width: '100%',
                                                justifyContent: 'space-between',
                                                marginBottom: 12
                                            }}
                                            wrap
                                        >
                                            <Title level={5} style={{ margin: 0 }}>
                                                <FileTextOutlined /> Файл и текст резюме
                                            </Title>
                                            <Button
                                                size="small"
                                                icon={<ReloadOutlined />}
                                                disabled={!currentDeal?.id}
                                                onClick={() =>
                                                    currentDeal?.id &&
                                                    refreshDealResume(currentDeal.id)
                                                }
                                            >
                                                Обновить из Bitrix
                                            </Button>
                                        </Space>

                                        {currentDeal?.resume?.url ||
                                        currentDeal?.resume?.local_available ||
                                        currentDeal?.resume?.has_file ? (
                                            <Space wrap style={{ marginBottom: 16 }}>
                                                {currentDeal.resume.url ? (
                                                    <Button
                                                        type="primary"
                                                        icon={<LinkOutlined />}
                                                        href={currentDeal.resume.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Открыть на источнике
                                                    </Button>
                                                ) : null}
                                                {currentDeal.resume.local_available ? (
                                                    <Space wrap>
                                                        <Button
                                                            icon={<EyeOutlined />}
                                                            onClick={e =>
                                                                openLocalResumeFile(
                                                                    currentDeal.id,
                                                                    e
                                                                )
                                                            }
                                                        >
                                                            Просмотр PDF
                                                        </Button>
                                                        <Button
                                                            icon={<DownloadOutlined />}
                                                            onClick={e =>
                                                                downloadLocalResumeFile(
                                                                    currentDeal.id,
                                                                    e
                                                                )
                                                            }
                                                        >
                                                            Скачать
                                                        </Button>
                                                        {currentDeal.resume.original_name ? (
                                                            <Text type="secondary">
                                                                {
                                                                    currentDeal.resume
                                                                        .original_name
                                                                }
                                                            </Text>
                                                        ) : null}
                                                    </Space>
                                                ) : currentDeal.resume.has_file ? (
                                                    <Text type="secondary">
                                                        Файл ещё не сохранён локально
                                                    </Text>
                                                ) : null}
                                            </Space>
                                        ) : (
                                            <Text type="secondary">
                                                Резюме в Bitrix не найдено
                                            </Text>
                                        )}

                                        <Card size="small" title="Текст резюме">
                                            {currentDeal?.resume?.extracted_text ? (
                                                <pre
                                                    style={{
                                                        whiteSpace: 'pre-wrap',
                                                        margin: 0,
                                                        maxHeight: '60vh',
                                                        overflow: 'auto',
                                                        fontFamily: 'inherit',
                                                        fontSize: 13,
                                                        lineHeight: 1.45
                                                    }}
                                                >
                                                    {currentDeal.resume.extracted_text}
                                                </pre>
                                            ) : (
                                                <Text type="secondary">
                                                    Текст пока не извлечён. Нажмите
                                                    «Обновить из Bitrix» для PDF.
                                                </Text>
                                            )}
                                        </Card>
                                    </div>
                                )
                            },
                            {
                                key: 'calls',
                                label: `Звонки${
                                    dealCalls?.length
                                        ? ` (${dealCalls.length})`
                                        : ''
                                }`,
                                children: (
                                    <div>
                                        <Space
                                            style={{
                                                width: '100%',
                                                justifyContent: 'space-between',
                                                marginBottom: 8
                                            }}
                                        >
                                            <Title level={5} style={{ margin: 0 }}>
                                                <PhoneOutlined /> Звонки АТС
                                                {currentDeal?.contact_phone
                                                    ? ` · ${currentDeal.contact_phone}`
                                                    : ''}
                                            </Title>
                                            <Button
                                                size="small"
                                                icon={<ReloadOutlined />}
                                                loading={dealCallsLoading}
                                                onClick={() =>
                                                    currentDeal?.id &&
                                                    loadDealCalls(currentDeal.id, {
                                                        refresh: true
                                                    })
                                                }
                                            >
                                                Обновить
                                            </Button>
                                        </Space>

                                        {dealCallsError ? (
                                            <Alert
                                                type="warning"
                                                showIcon
                                                message={dealCallsError}
                                                style={{ marginBottom: 8 }}
                                            />
                                        ) : null}

                                        <Spin spinning={dealCallsLoading}>
                                            <Table
                                                size="small"
                                                rowKey={(row, idx) =>
                                                    `${row.record_file_name || row.start || idx}`
                                                }
                                                dataSource={dealCalls}
                                                pagination={false}
                                                locale={{
                                                    emptyText: 'Звонков нет'
                                                }}
                                                columns={[
                                                    {
                                                        title: 'Дата',
                                                        dataIndex: 'start',
                                                        width: 140,
                                                        render: v =>
                                                            v
                                                                ? dayjs(v).format(
                                                                      'DD.MM.YYYY HH:mm'
                                                                  )
                                                                : '—'
                                                    },
                                                    {
                                                        title: 'Направление',
                                                        dataIndex: 'direction',
                                                        width: 110
                                                    },
                                                    {
                                                        title: 'Номер',
                                                        dataIndex: 'phone',
                                                        width: 130
                                                    },
                                                    {
                                                        title: 'Сек.',
                                                        dataIndex: 'duration',
                                                        width: 70
                                                    },
                                                    {
                                                        title: 'Запись',
                                                        key: 'rec',
                                                        render: (_v, row) => {
                                                            if (
                                                                !row.record_file_name
                                                            ) {
                                                                return '—';
                                                            }
                                                            const url =
                                                                audioUrls[
                                                                    row.record_file_name
                                                                ];
                                                            if (url) {
                                                                return (
                                                                    <audio
                                                                        controls
                                                                        preload="none"
                                                                        src={url}
                                                                        style={{
                                                                            width: 180,
                                                                            height: 32
                                                                        }}
                                                                        onClick={e =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                            return (
                                                                <Button
                                                                    size="small"
                                                                    icon={
                                                                        <SoundOutlined />
                                                                    }
                                                                    onClick={async e => {
                                                                        e.stopPropagation();
                                                                        try {
                                                                            await loadRecordingAudio(
                                                                                row.record_file_name
                                                                            );
                                                                        } catch (audioError) {
                                                                            message.error(
                                                                                audioError
                                                                                    .response
                                                                                    ?.data
                                                                                    ?.error ||
                                                                                    'Не удалось загрузить запись'
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Слушать
                                                                </Button>
                                                            );
                                                        }
                                                    }
                                                ]}
                                            />
                                        </Spin>
                                    </div>
                                )
                            },
                            {
                                key: 'comments',
                                label: `Комментарии${
                                    currentDeal?.comments?.length
                                        ? ` (${currentDeal.comments.length})`
                                        : ''
                                }`,
                                children: (
                                    <div>
                                        <Space.Compact
                                            style={{
                                                width: '100%',
                                                marginBottom: 16
                                            }}
                                        >
                                            <Input
                                                value={commentText}
                                                onChange={e =>
                                                    setCommentText(e.target.value)
                                                }
                                                placeholder="Комментарий (уйдёт в CRM и Bitrix)"
                                                onPressEnter={addComment}
                                            />
                                            <Button
                                                type="primary"
                                                loading={commentSaving}
                                                onClick={addComment}
                                            >
                                                Добавить
                                            </Button>
                                        </Space.Compact>

                                        {(currentDeal?.comments || []).length ===
                                        0 ? (
                                            <Text type="secondary">
                                                Комментариев пока нет
                                            </Text>
                                        ) : (
                                            <Space
                                                direction="vertical"
                                                style={{ width: '100%' }}
                                                size={8}
                                            >
                                                {currentDeal.comments.map(
                                                    comment => (
                                                        <Card
                                                            key={comment.id}
                                                            size="small"
                                                        >
                                                            <Space wrap>
                                                                <Text strong>
                                                                    {comment.user_name ||
                                                                        '—'}
                                                                </Text>
                                                                <Tag
                                                                    color={
                                                                        comment.source ===
                                                                        'bitrix'
                                                                            ? 'purple'
                                                                            : 'cyan'
                                                                    }
                                                                >
                                                                    {comment.source ===
                                                                    'bitrix'
                                                                        ? 'Bitrix'
                                                                        : 'CRM'}
                                                                </Tag>
                                                            </Space>
                                                            <div
                                                                style={{
                                                                    whiteSpace:
                                                                        'pre-wrap',
                                                                    marginTop: 4
                                                                }}
                                                            >
                                                                {comment.text}
                                                            </div>
                                                            <Text type="secondary">
                                                                {comment.bitrix_created_at ||
                                                                comment.created_at
                                                                    ? dayjs(
                                                                          comment.bitrix_created_at ||
                                                                              comment.created_at
                                                                      ).format(
                                                                          'DD.MM.YYYY HH:mm'
                                                                      )
                                                                    : ''}
                                                            </Text>
                                                        </Card>
                                                    )
                                                )}
                                            </Space>
                                        )}
                                    </div>
                                )
                            },
                            {
                                key: 'history',
                                label: 'История',
                                children: (
                                    <div>
                                        <Title level={5} style={{ marginTop: 0 }}>
                                            История стадий Bitrix
                                        </Title>
                                        {(currentDeal?.history || []).length ===
                                        0 ? (
                                            <Text type="secondary">
                                                История пуста
                                            </Text>
                                        ) : (
                                            <Timeline
                                                items={(
                                                    currentDeal.history || []
                                                ).map(item => ({
                                                    children: (
                                                        <div>
                                                            <div>
                                                                {item.old_stage_name ||
                                                                    '—'}{' '}
                                                                →{' '}
                                                                {item.new_stage_name ||
                                                                    '—'}
                                                            </div>
                                                            <Text type="secondary">
                                                                {item.changed_at
                                                                    ? dayjs(
                                                                          item.changed_at
                                                                      ).format(
                                                                          'DD.MM.YYYY HH:mm'
                                                                      )
                                                                    : ''}
                                                                {item.duration_hours !=
                                                                null
                                                                    ? ` · ${item.duration_hours} ч`
                                                                    : ''}
                                                            </Text>
                                                        </div>
                                                    )
                                                }))}
                                            />
                                        )}
                                    </div>
                                )
                            }
                        ]}
                    />
                </Spin>
    );

    return (
        <Layout fullHeight>
            <div className="hr-page-shell">
                <aside
                    className={`tm-filter hr-page-filter${
                        filtersOpen ? '' : ' is-collapsed'
                    }`}
                    style={
                        filtersOpen
                            ? {
                                  flex: `0 0 ${paneLayout.filterPx}px`,
                                  width: paneLayout.filterPx,
                                  minWidth: paneLayout.filterPx,
                                  maxWidth: paneLayout.filterPx
                              }
                            : undefined
                    }
                >
                    <div className="tm-filter-header">
                        <div className="tm-filter-header-text">
                            <span className="tm-filter-kicker">
                                Bitrix24
                            </span>
                            <Text className="tm-filter-title">
                                Фильтр
                            </Text>
                        </div>
                        <Tooltip
                            title={
                                filtersOpen
                                    ? 'Скрыть фильтр'
                                    : 'Показать фильтр'
                            }
                        >
                            <Button
                                type="text"
                                size="small"
                                className="tm-filter-collapse"
                                icon={
                                    filtersOpen ? (
                                        <MenuFoldOutlined />
                                    ) : (
                                        <MenuUnfoldOutlined />
                                    )
                                }
                                onClick={() => {
                                    setFiltersOpen(open => {
                                        const next = !open;
                                        persistViewConfig({
                                            filtersOpen: next
                                        });
                                        return next;
                                    });
                                }}
                            />
                        </Tooltip>
                    </div>

                    <div className="tm-filter-body hr-page-filter-body">
                        <div className="tm-filter-templates">
                            <Text className="tm-filter-hint">
                                Шаблоны фильтра
                            </Text>
                            <div className="tm-filter-templates-row">
                                <Select
                                    className="tm-filter-template-select"
                                    size="small"
                                    allowClear
                                    placeholder="Выбрать шаблон"
                                    value={
                                        activeFilterTemplateId ||
                                        undefined
                                    }
                                    options={filterTemplates.map(
                                        item => ({
                                            value: item.id,
                                            label: item.name
                                        })
                                    )}
                                    onChange={value => {
                                        if (!value) {
                                            setActiveFilterTemplateId(
                                                null
                                            );
                                            return;
                                        }
                                        applyFilterTemplate(value);
                                    }}
                                />
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    title="Сохранить текущий фильтр как шаблон"
                                    onClick={() => {
                                        setTemplateName('');
                                        setTemplateModalOpen(true);
                                    }}
                                />
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    title="Удалить выбранный шаблон"
                                    disabled={!activeFilterTemplateId}
                                    onClick={deleteFilterTemplate}
                                />
                            </div>
                        </div>

                        <Text className="tm-filter-hint">
                            Поля фильтра
                        </Text>
                        <Select
                            className="tm-filter-picker"
                            mode="multiple"
                            allowClear
                            showSearch
                            size="small"
                            optionFilterProp="label"
                            placeholder="Добавить поле"
                            value={draftFilterFields}
                            options={HR_FILTER_FIELD_OPTIONS}
                            onChange={value => {
                                setDraftFilterFields(value);
                                persistViewConfig({
                                    filterFields: value
                                });
                            }}
                        />

                        <Divider className="tm-filter-divider" />

                        {(draftFilterFields || []).length === 0 ? (
                            <Empty
                                className="tm-filter-empty"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="Добавьте поля фильтра сверху"
                            />
                        ) : (
                            <Space
                                direction="vertical"
                                size={12}
                                style={{ width: '100%' }}
                            >
                                {draftFilterFields.map(fieldKey => {
                                    const label =
                                        HR_FILTER_FIELD_OPTIONS.find(
                                            item =>
                                                item.value ===
                                                fieldKey
                                        )?.label || fieldKey;
                                    return (
                                        <div
                                            key={fieldKey}
                                            className="tm-filter-field"
                                        >
                                            <div className="tm-filter-field-label">
                                                {label}
                                            </div>
                                            <div className="tm-filter-field-control">
                                                {renderHrFilterField(
                                                    fieldKey
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </Space>
                        )}
                    </div>

                    <div className="tm-filter-footer">
                        <Button block onClick={resetBoardFilters}>
                            Сбросить
                        </Button>
                        <Button
                            block
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={applyFilters}
                        >
                            Применить
                        </Button>
                    </div>
                </aside>

                {!isMobile && filtersOpen ? (
                    <div
                        className="tm-pane-resizer"
                        title="Тяните, чтобы изменить ширину фильтра"
                        onMouseDown={event =>
                            startPaneResize('filter', event)
                        }
                    />
                ) : null}

                <div className="hr-page-main">
                    <Space
                        className="hr-page-toolbar"
                        style={{
                            width: '100%',
                            justifyContent: 'space-between',
                            marginBottom: 12,
                            flexShrink: 0
                        }}
                        wrap
                    >
                        <Space wrap align="start">
                            {!filtersOpen ? (
                                <Button
                                    icon={<MenuUnfoldOutlined />}
                                    onClick={() => {
                                        setFiltersOpen(true);
                                        persistViewConfig({
                                            filtersOpen: true
                                        });
                                    }}
                                >
                                    Показать фильтр
                                </Button>
                            ) : null}
                            <div>
                                <Title
                                    level={3}
                                    style={{ margin: 0 }}
                                >
                                    Сделки HR (Bitrix24)
                                </Title>
                                <Text type="secondary">
                                    Стадии и сделки из Bitrix ·
                                    всего: {total}
                                    {boardView === 'kanban'
                                        ? ' · ПКМ + движение — прокрутка стадий'
                                        : ''}
                                </Text>
                            </div>
                        </Space>

                        <Space wrap>
                            {!isMobile ? (
                                <Space size={6}>
                                    <Text type="secondary">
                                        Отдельное окно
                                    </Text>
                                    <Switch
                                        checked={detailInDrawer}
                                        onChange={checked => {
                                            setDetailInDrawer(checked);
                                            persistViewConfig({
                                                detailInDrawer: checked
                                            });
                                        }}
                                    />
                                </Space>
                            ) : null}
                            <Segmented
                                value={boardView}
                                onChange={value => {
                                    setBoardView(value);
                                    persistViewConfig({
                                        boardView: value
                                    });
                                }}
                                options={[
                                    {
                                        value: 'kanban',
                                        icon: (
                                            <AppstoreOutlined />
                                        ),
                                        label: 'Канбан'
                                    },
                                    {
                                        value: 'list',
                                        icon: (
                                            <UnorderedListOutlined />
                                        ),
                                        label: 'Список'
                                    }
                                ]}
                            />
                            <Tooltip
                                title={
                                    avitoStatus
                                        ? `Avito client_credentials · apps=${avitoStatus.applications_count || 0} · token=${avitoStatus.stored_token?.expires_at || 'нет'}`
                                        : 'Avito: client_credentials → отклики (без OAuth redirect)'
                                }
                            >
                                <Button
                                    loading={avitoBusy}
                                    onClick={runAvitoFetch}
                                >
                                    Avito
                                </Button>
                            </Tooltip>
                            <Tooltip
                                title={
                                    hhStatus
                                        ? `HH → hh_negotiations · count=${hhStatus.negotiations_count || 0} · grant=${hhStatus.stored_token?.grant_type || 'нет OAuth'}`
                                        : 'HH: отклики в БД без сделок (один раз нужен OAuth менеджера)'
                                }
                            >
                                <Button
                                    loading={hhBusy}
                                    onClick={runHhFetch}
                                >
                                    HH
                                </Button>
                            </Tooltip>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    fetchBoard();
                                    fetchMeta();
                                }}
                                loading={loading}
                            >
                                Обновить
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    createForm.resetFields();
                                    if (stages[0]?.id) {
                                        createForm.setFieldsValue(
                                            {
                                                stage_id:
                                                    stages[0].id
                                            }
                                        );
                                    }
                                    setCreateOpen(true);
                                }}
                            >
                                Создать
                            </Button>
                        </Space>
                    </Space>

                    <div className="hr-page-board-wrap">
                    {boardView === 'list' ? (
                        <HrDealsList
                            deals={flatDeals}
                            loading={loading}
                            onDealClick={deal =>
                                openDeal(deal.id)
                            }
                        />
                    ) : (
                    <Spin spinning={loading} style={{ height: '100%', width: '100%' }} wrapperClassName="hr-board-spin">
                        <div
                            ref={boardRef}
                            className={`hr-kanban-board${
                                boardPanning ? ' is-panning' : ''
                            }`}
                            onMouseDown={event => {
                                if (event.button !== 2) {
                                    return;
                                }
                                const el = boardRef.current;
                                if (!el) {
                                    return;
                                }
                                panRef.current = {
                                    active: true,
                                    startX: event.clientX,
                                    startScroll: el.scrollLeft,
                                    moved: false
                                };
                                setBoardPanning(true);
                                event.preventDefault();
                            }}
                            onContextMenu={event => {
                                event.preventDefault();
                            }}
                        >
                            {stages.length === 0 ? (
                                <Empty description="Стадии Bitrix не найдены" />
                            ) : (
                                stages.map(stage => (
                                    <div
                                        key={stage.id}
                                        className={`hr-kanban-column${
                                            dragOverStageId ===
                                            stage.id
                                                ? ' is-over'
                                                : ''
                                        }`}
                                        style={{
                                            borderTopColor:
                                                stage.color ||
                                                '#1890ff'
                                        }}
                                        onDragOver={e => {
                                            e.preventDefault();
                                            setDragOverStageId(
                                                stage.id
                                            );
                                        }}
                                        onDragLeave={() => {
                                            if (
                                                dragOverStageId ===
                                                stage.id
                                            ) {
                                                setDragOverStageId(
                                                    null
                                                );
                                            }
                                        }}
                                        onDrop={e => {
                                            e.preventDefault();
                                            onDropToStage(
                                                stage.id
                                            );
                                        }}
                                    >
                                        <div className="hr-kanban-column-header">
                                            <Space>
                                                <span
                                                    className="hr-kanban-dot"
                                                    style={{
                                                        background:
                                                            stage.color ||
                                                            '#1890ff'
                                                    }}
                                                />
                                                <Text strong>
                                                    {stage.name}
                                                </Text>
                                                <Tag
                                                    color={
                                                        stage.color ||
                                                        'blue'
                                                    }
                                                >
                                                    {(
                                                        stage.deals ||
                                                        []
                                                    ).length}
                                                </Tag>
                                            </Space>
                                        </div>

                                        <div className="hr-kanban-column-body">
                                            {(stage.deals || [])
                                                .length === 0 ? (
                                                <div className="hr-kanban-empty">
                                                    Перетащите сюда
                                                </div>
                                            ) : (
                                                stage.deals.map(
                                                    deal => (
                                                        <Card
                                                            key={
                                                                deal.id
                                                            }
                                                            size="small"
                                                            className={`hr-kanban-card${
                                                                String(
                                                                    dragDealId
                                                                ) ===
                                                                String(
                                                                    deal.id
                                                                )
                                                                    ? ' is-dragging'
                                                                    : ''
                                                            }`}
                                                            style={{
                                                                borderLeftColor:
                                                                    stage.color ||
                                                                    '#1890ff'
                                                            }}
                                                            draggable={
                                                                stage.id !==
                                                                '__unknown__'
                                                            }
                                                            onDragStart={e => {
                                                                e.dataTransfer.effectAllowed =
                                                                    'move';
                                                                try {
                                                                    e.dataTransfer.setData(
                                                                        'text/plain',
                                                                        String(
                                                                            deal.id
                                                                        )
                                                                    );
                                                                } catch (
                                                                    _error
                                                                ) {
                                                                    // ignore
                                                                }
                                                                setDragDealId(
                                                                    deal.id
                                                                );
                                                            }}
                                                            onDragEnd={() => {
                                                                setDragDealId(
                                                                    null
                                                                );
                                                                setDragOverStageId(
                                                                    null
                                                                );
                                                            }}
                                                            onClick={() =>
                                                                openDeal(
                                                                    deal.id
                                                                )
                                                            }
                                                        >
                                                            <div className="hr-kanban-card-title">
                                                                {deal.title}
                                                            </div>

                                                            {deal.city ? (
                                                                <div className="hr-kanban-card-meta">
                                                                    <EnvironmentOutlined />{' '}
                                                                    {deal.city}
                                                                </div>
                                                            ) : null}

                                                            {deal.next_calendar_event ? (
                                                                <div className="hr-kanban-card-meta">
                                                                    <CalendarOutlined />
                                                                    <span>
                                                                        {
                                                                            deal
                                                                                .next_calendar_event
                                                                                .type_name
                                                                        }
                                                                        {': '}
                                                                        {formatDealEventWhen(
                                                                            deal.next_calendar_event
                                                                        )}
                                                                        {Number(
                                                                            deal.calendar_events_count
                                                                        ) > 1
                                                                            ? ` · ещё ${
                                                                                  Number(
                                                                                      deal.calendar_events_count
                                                                                  ) -
                                                                                  1
                                                                              }`
                                                                            : ''}
                                                                    </span>
                                                                </div>
                                                            ) : null}

                                                            {deal.contact_phone ? (
                                                                <div className="hr-kanban-card-meta">
                                                                    <PhoneOutlined />
                                                                    <a
                                                                        href={`tel:${deal.contact_phone}`}
                                                                        onClick={e =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    >
                                                                        {
                                                                            deal.contact_phone
                                                                        }
                                                                    </a>
                                                                </div>
                                                            ) : null}

                                                            {(() => {
                                                                const callInfo =
                                                                    dealCallsCache[
                                                                        deal
                                                                            .id
                                                                    ] || {
                                                                        count:
                                                                            Number(
                                                                                deal.calls_count
                                                                            ) ||
                                                                            0,
                                                                        recorded:
                                                                            Number(
                                                                                deal.calls_recorded
                                                                            ) ||
                                                                            0,
                                                                        lastDate:
                                                                            deal.last_call_at ||
                                                                            null
                                                                    };

                                                                if (
                                                                    !deal.contact_phone &&
                                                                    !callInfo.count
                                                                ) {
                                                                    return null;
                                                                }

                                                                return (
                                                                    <div className="hr-kanban-card-meta">
                                                                        <SoundOutlined />
                                                                        <Tag
                                                                            color={
                                                                                callInfo.count >
                                                                                0
                                                                                    ? 'blue'
                                                                                    : 'default'
                                                                            }
                                                                            style={{
                                                                                marginInlineEnd: 0
                                                                            }}
                                                                        >
                                                                            {
                                                                                callInfo.count
                                                                            }{' '}
                                                                            зв.
                                                                            {callInfo.recorded
                                                                                ? ` · ${callInfo.recorded} зап.`
                                                                                : ''}
                                                                        </Tag>
                                                                    </div>
                                                                );
                                                            })()}

                                                            {deal.contact_name ? (
                                                                <div className="hr-kanban-card-meta">
                                                                    <UserOutlined />{' '}
                                                                    {
                                                                        deal.contact_name
                                                                    }
                                                                </div>
                                                            ) : null}

                                                            {deal.source_name ? (
                                                                <div className="hr-kanban-card-meta">
                                                                    {
                                                                        deal.source_name
                                                                    }
                                                                </div>
                                                            ) : null}

                                                            <div className="hr-kanban-card-footer">
                                                                <Text type="secondary">
                                                                    {deal.assigned_name ||
                                                                        '—'}
                                                                </Text>
                                                                <Text type="secondary">
                                                                    <ClockCircleOutlined />{' '}
                                                                    {deal.date_modify
                                                                        ? dayjs(
                                                                              deal.date_modify
                                                                          ).format(
                                                                              'DD.MM.YYYY HH:mm'
                                                                          )
                                                                        : '—'}
                                                                </Text>
                                                            </div>
                                                        </Card>
                                                    )
                                                )
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </Spin>
                    )}
                    </div>
                </div>

                {splitDetail ? (
                    <>
                    <div
                        className="tm-pane-resizer"
                        title="Тяните, чтобы изменить ширину карточки"
                        onMouseDown={event =>
                            startPaneResize('detail', event)
                        }
                    />
                    <div
                        className="hr-page-detail-pane"
                        style={{
                            flex: `0 0 ${paneLayout.detailPx}px`,
                            width: paneLayout.detailPx,
                            minWidth: paneLayout.detailPx,
                            maxWidth: paneLayout.detailPx
                        }}
                    >
                        <div className="hr-page-detail-header">
                            <div className="hr-page-detail-title">
                                {dealDetailTitle}
                            </div>
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                onClick={closeDealPanel}
                            />
                        </div>
                        <div className="hr-page-detail-body">
                            {dealDetailBody}
                        </div>
                        <div className="hr-page-detail-footer">
                            {dealDetailActions}
                        </div>
                    </div>
                    </>
                ) : null}
            </div>

            <Modal
                title="Новая сделка Bitrix"
                open={createOpen}
                onCancel={() =>
                    !creating && setCreateOpen(false)
                }
                onOk={createDeal}
                confirmLoading={creating}
                okText="Создать"
                destroyOnClose
                width={640}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    requiredMark={false}
                >
                    <Form.Item
                        name="title"
                        label="Название / ФИО"
                        rules={[
                            {
                                required: true,
                                message: 'Укажите название'
                            }
                        ]}
                    >
                        <Input placeholder="ФИО кандидата" />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="city"
                                label="Город"
                            >
                                <Input placeholder="Город кандидата" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="contact_phone"
                                label="Телефон"
                            >
                                <Input placeholder="+7..." />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="stage_id"
                                label="Стадия"
                            >
                                <Select
                                    options={stages
                                        .filter(
                                            stage =>
                                                stage.id !==
                                                '__unknown__'
                                        )
                                        .map(stage => ({
                                            value: stage.id,
                                            label: stage.name
                                        }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="assigned_by_id"
                                label="Ответственный"
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={userOptions}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="source_id"
                                label="Источник"
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={sourceOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="company_id"
                                label="Компания"
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={companyOptions}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                </Form>
            </Modal>

            {useDetailDrawer ? (
                <Drawer
                    title={dealDetailTitle}
                    open={dealOpen}
                    width={isMobile ? '100%' : '80%'}
                    onClose={closeDealPanel}
                    extra={dealDetailActions}
                    destroyOnClose={false}
                >
                    {dealDetailBody}
                </Drawer>
            ) : null}

            <Modal
                title="Событие в календарь"
                open={eventOpen}
                onCancel={() => !eventSaving && setEventOpen(false)}
                onOk={createDealEvent}
                confirmLoading={eventSaving}
                okText="Создать"
                destroyOnClose
                width={560}
            >
                <Form form={eventForm} layout="vertical">
                    <Form.Item name="type" label="Тип" rules={[{ required: true }]}>
                        <Select
                            options={[
                                { value: 'meeting', label: 'Встреча' },
                                { value: 'event', label: 'Событие' }
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        name="title"
                        label="Название"
                        rules={[{ required: true, message: 'Укажите название' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="all_day"
                        label="Весь день"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                    <Form.Item
                        name="range"
                        label={eventAllDay ? 'Даты' : 'Начало и окончание'}
                        rules={[{ required: true, message: 'Укажите время' }]}
                    >
                        <RangePicker
                            showTime={!eventAllDay}
                            format={
                                eventAllDay
                                    ? 'DD.MM.YYYY'
                                    : 'DD.MM.YYYY HH:mm'
                            }
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                    <Form.Item name="participant_ids" label="Сотрудники">
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={colleagues.map(u => ({
                                value: u.id,
                                label: u.name
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="location" label="Место / город">
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Комментарий">
                        <TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={
                    resumePreview.name || 'Просмотр резюме'
                }
                open={resumePreview.open}
                onCancel={closeResumePreview}
                width="90vw"
                style={{ top: 24 }}
                zIndex={2100}
                getContainer={() => document.body}
                maskClosable={false}
                destroyOnClose={false}
                styles={{
                    body: {
                        height: '75vh',
                        padding: 12,
                        overflow: 'hidden'
                    }
                }}
                footer={
                    <Space>
                        <Button onClick={closeResumePreview}>
                            Закрыть
                        </Button>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            disabled={!resumePreview.url}
                            onClick={e => {
                                e.stopPropagation();

                                if (!resumePreview.url) {
                                    return;
                                }

                                const link =
                                    document.createElement(
                                        'a'
                                    );
                                link.href = resumePreview.url;
                                link.download =
                                    resumePreview.name ||
                                    'resume';
                                link.rel = 'noopener';
                                document.body.appendChild(
                                    link
                                );
                                link.click();
                                link.remove();
                            }}
                        >
                            Скачать
                        </Button>
                    </Space>
                }
            >
                {resumePreview.url ? (
                    String(resumePreview.mime || '')
                        .startsWith('image/') ? (
                        <div className="hr-resume-preview-image-wrap">
                            <img
                                className="hr-resume-preview-image"
                                src={resumePreview.url}
                                alt={
                                    resumePreview.name ||
                                    'resume'
                                }
                            />
                        </div>
                    ) : String(
                          resumePreview.mime || ''
                      ).includes('pdf') ? (
                        <iframe
                            className="hr-resume-preview-frame"
                            title="Просмотр резюме"
                            src={resumePreview.url}
                        />
                    ) : String(
                          resumePreview.mime || ''
                      ).startsWith('text/') ? (
                        <iframe
                            className="hr-resume-preview-frame"
                            title="Просмотр резюме"
                            src={resumePreview.url}
                        />
                    ) : (
                        <div className="hr-resume-preview-fallback">
                            <Empty
                                description={
                                    <span>
                                        Предпросмотр для этого
                                        типа файла недоступен
                                        {resumePreview.mime
                                            ? ` (${resumePreview.mime})`
                                            : ''}
                                        . Скачайте файл.
                                    </span>
                                }
                            />
                        </div>
                    )
                ) : (
                    <div className="hr-resume-preview-fallback">
                        <Spin />
                    </div>
                )}
            </Modal>
            <Modal
                title="Сохранить шаблон фильтра"
                open={templateModalOpen}
                onCancel={() => setTemplateModalOpen(false)}
                onOk={saveFilterTemplate}
                confirmLoading={templateSaving}
                okText="Сохранить"
                cancelText="Отмена"
                destroyOnClose
            >
                <Text type="secondary">
                    Сохранятся выбранные поля и их текущие
                    значения
                </Text>
                <Input
                    style={{ marginTop: 12 }}
                    placeholder="Название шаблона"
                    value={templateName}
                    maxLength={80}
                    onChange={e =>
                        setTemplateName(e.target.value)
                    }
                    onPressEnter={saveFilterTemplate}
                    autoFocus
                />
            </Modal>
        </Layout>
    );
};


export default HrDealsKanbanPage;
